import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Loader2, Trash2, Video as VideoIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const detectPlatform = (url) => {
  const u = (url || '').toLowerCase();
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
  if (u.includes('soundcloud.com')) return 'soundcloud';
  if (u.includes('vimeo.com')) return 'vimeo';
  if (u.includes('spotify.com')) return 'spotify';
  if (u.includes('tiktok.com')) return 'tiktok';
  return 'other';
};

/**
 * InlineVideoEditor — quick edit for a SINGLE video inside an artist's videos[] array.
 *
 * props:
 *   - artist: full artist object (must contain `id`, `videos`)
 *   - videoIndex: index of the video inside artist.videos
 *   - adminPwd: admin password from InlineAdminGate
 *   - onClose / onSaved
 */
export default function InlineVideoEditor({ artist, videoIndex, adminPwd, onClose, onSaved }) {
  const current = (artist?.videos || [])[videoIndex] || { title: '', url: '', platform: 'youtube' };
  const [title, setTitle] = useState(current.title || '');
  const [url, setUrl] = useState(current.url || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const buildPayload = (newVideos) => {
    const fd = new FormData();
    fd.append('password', adminPwd);
    fd.append('name', artist.name || '');
    fd.append('videos_json', JSON.stringify(newVideos));
    return fd;
  };

  const save = async () => {
    setError(''); setSaving(true);
    try {
      const newVideos = [...(artist.videos || [])];
      newVideos[videoIndex] = {
        title: title.trim(),
        url: url.trim(),
        platform: detectPlatform(url),
      };
      const r = await fetch(`${API}/admin/artists/${artist.id}`, { method: 'PUT', body: buildPayload(newVideos) });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || `Erreur ${r.status}`); return;
      }
      const updated = await r.json();
      onSaved && onSaved(updated);
      onClose();
    } catch (e) {
      setError(e.message || 'Erreur reseau');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer cette video ?')) return;
    setError(''); setSaving(true);
    try {
      const newVideos = (artist.videos || []).filter((_, i) => i !== videoIndex);
      const r = await fetch(`${API}/admin/artists/${artist.id}`, { method: 'PUT', body: buildPayload(newVideos) });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || `Erreur ${r.status}`); return;
      }
      const updated = await r.json();
      onSaved && onSaved(updated);
      onClose();
    } catch (e) {
      setError(e.message || 'Erreur reseau');
    } finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[130] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="inline-video-editor">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-red-500/30 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div>
            <p className="text-[10px] text-red-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
              <VideoIcon size={11} /> Edition rapide video
            </p>
            <h3 className="text-base font-black text-white">{artist?.name}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-2"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Titre</p>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="video-edit-title"
              placeholder="Ex: PAPYON ELECTRO (Official Video)"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-red-500" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">URL</p>
            <input value={url} onChange={(e) => setUrl(e.target.value)}
              data-testid="video-edit-url"
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-red-500" />
            <p className="text-[10px] text-zinc-500 mt-1">
              Plateforme detectee : <span className="text-red-300 font-bold">{detectPlatform(url)}</span>
            </p>
          </div>
          {error && <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-300">{error}</div>}
        </div>

        <div className="flex justify-between items-center gap-2 p-4 border-t border-zinc-800">
          <button onClick={remove} disabled={saving}
            data-testid="video-edit-delete"
            className="px-3 py-2 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-300 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50">
            <Trash2 size={11} /> Supprimer
          </button>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-xs">Annuler</button>
            <button onClick={save} disabled={saving || !url.trim()}
              data-testid="video-edit-save"
              className="px-4 py-2 bg-red-500 hover:bg-red-400 text-white font-bold rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Sauvegarder
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
